import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'

interface Notification {
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
    duration?: number
}

export type StaffViewMode = 'admin' | 'user'

/** Prefill for StartTableForm when opening table detail from a booking. */
export interface StartTableInitialPlayer {
    userId?: number
    fullName: string
    isGuest: boolean
    guestName?: string
}

export interface OpenTableDetailModalPayload {
    tableId: number
    openStartForm?: boolean
    initialPlayers?: StartTableInitialPlayer[]
}

interface UIState {
    notifications: Notification[]
    modals: {
        tableDetail: {
            isOpen: boolean
            tableId: number | null
            openStartForm: boolean
            initialPlayers: StartTableInitialPlayer[] | null
        }
        startTable: { isOpen: boolean; tableId: number | null }
        userSearch: { isOpen: boolean }
        tariffEdit: { isOpen: boolean; tariffId: number | null }
    }
    sidebarCollapsed: boolean
    theme: 'light' | 'dark'
    staffViewMode: StaffViewMode
    isRefreshing: boolean
}

const initialState: UIState = {
    notifications: [],
    modals: {
        tableDetail: {isOpen: false, tableId: null, openStartForm: false, initialPlayers: null},
        startTable: {isOpen: false, tableId: null},
        userSearch: {isOpen: false},
        tariffEdit: {isOpen: false, tariffId: null}
    },
    sidebarCollapsed: false,
    theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
    staffViewMode: (localStorage.getItem('staffViewMode') as StaffViewMode) || 'admin',
    isRefreshing: false
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        // Notifications
        addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
            const notification: Notification = {
                ...action.payload,
                id: `${Date.now()}-${Math.random()}`,
                duration: action.payload.duration || 2000
            }
            state.notifications.push(notification)
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter(n => n.id !== action.payload)
        },
        clearNotifications: (state) => {
            state.notifications = []
        },

        // Table Detail Modal
        openTableDetailModal: (state, action: PayloadAction<number | OpenTableDetailModalPayload>) => {
            const payload = typeof action.payload === 'number'
                ? { tableId: action.payload }
                : action.payload
            state.modals.tableDetail = {
                isOpen: true,
                tableId: payload.tableId,
                openStartForm: Boolean(payload.openStartForm),
                initialPlayers: payload.initialPlayers?.length ? payload.initialPlayers : null,
            }
        },
        closeTableDetailModal: (state) => {
            state.modals.tableDetail = {
                isOpen: false,
                tableId: null,
                openStartForm: false,
                initialPlayers: null,
            }
        },

        // Start Table Modal
        openStartTableModal: (state, action: PayloadAction<number>) => {
            state.modals.startTable = {isOpen: true, tableId: action.payload}
        },
        closeStartTableModal: (state) => {
            state.modals.startTable = {isOpen: false, tableId: null}
        },

        // User Search Modal
        openUserSearchModal: (state) => {
            state.modals.userSearch = {isOpen: true}
        },
        closeUserSearchModal: (state) => {
            state.modals.userSearch = {isOpen: false}
        },

        // Tariff Edit Modal
        openTariffEditModal: (state, action: PayloadAction<number | null>) => {
            state.modals.tariffEdit = {isOpen: true, tariffId: action.payload}
        },
        closeTariffEditModal: (state) => {
            state.modals.tariffEdit = {isOpen: false, tariffId: null}
        },

        // UI Settings
        toggleSidebar: (state) => {
            state.sidebarCollapsed = !state.sidebarCollapsed
        },
        setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
            state.sidebarCollapsed = action.payload
        },
        setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
            state.theme = action.payload
            localStorage.setItem('theme', action.payload)
        },
        toggleTheme: (state) => {
            state.theme = state.theme === 'light' ? 'dark' : 'light'
            localStorage.setItem('theme', state.theme)
        },
        setStaffViewMode: (state, action: PayloadAction<StaffViewMode>) => {
            state.staffViewMode = action.payload
            localStorage.setItem('staffViewMode', action.payload)
        },

        // Global Refresh
        setRefreshing: (state, action: PayloadAction<boolean>) => {
            state.isRefreshing = action.payload
        }
    }
})

export const {
    addNotification,
    removeNotification,
    clearNotifications,
    openTableDetailModal,
    closeTableDetailModal,
    openStartTableModal,
    closeStartTableModal,
    openUserSearchModal,
    closeUserSearchModal,
    openTariffEditModal,
    closeTariffEditModal,
    toggleSidebar,
    setSidebarCollapsed,
    setTheme,
    toggleTheme,
    setStaffViewMode,
    setRefreshing
} = uiSlice.actions

export default uiSlice.reducer